import { useState, useEffect, useCallback } from 'react';
import subscriptionService from '../services/subscriptionService';
import { useAuth } from '../contexts/AuthContext';

const CACHE_KEY = 'vidal_subscription_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL) return null;
    return cached.data;
  } catch {
    return null;
  }
}

function setCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
}

export default function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const isGod = user?.cargo === 'god';
  const isLegacy = !user?.billingClass; // admin/user sem billingClass = acesso vitalício

  const refresh = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const [subRes, usageRes] = await Promise.all([
        subscriptionService.getMySubscription().catch(() => null),
        subscriptionService.getUsage().catch(() => null),
      ]);

      const data = {
        subscription: subRes?.subscription || null,
        plan: subRes?.plan || null,
        usage: usageRes?.usage || null,
      };

      setSubscription(data.subscription);
      setPlan(data.plan);
      setUsage(data.usage);
      setCache(data);
    } catch {
      // Keep cached data if available
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Load from cache first
    const cached = getCached();
    if (cached) {
      setSubscription(cached.subscription);
      setPlan(cached.plan);
      setUsage(cached.usage);
      setLoading(false);
    }

    // Then refresh from server
    refresh();
  }, [user, refresh]);

  // Derived state
  const status = subscription?.status || 'none';
  const isTrial = status === 'trial';
  const isActive = ['active', 'trial'].includes(status) || isLegacy;
  const isExpired = ['trial_expired', 'canceled', 'suspended'].includes(status);
  const isPastDue = status === 'past_due';

  // Trial days remaining
  let trialDaysLeft = null;
  if (isTrial && subscription?.trialEndsAt) {
    const diff = new Date(subscription.trialEndsAt) - new Date();
    trialDaysLeft = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  // Feature access check
  const canAccess = useCallback((feature) => {
    if (isGod) return true;
    if (!isActive) return false;
    if (!plan?.features) return true; // No feature restrictions defined
    return plan.features[feature] !== false;
  }, [isGod, isActive, plan]);

  // Usage limit check
  const getUsageInfo = useCallback((metric) => {
    if (isGod) return { used: 0, limit: -1, pct: 0, isUnlimited: true };
    if (!plan?.limits || !usage) return { used: 0, limit: -1, pct: 0, isUnlimited: true };

    const limitMap = {
      xmlDownloads: plan.limits.xmlDownloadsPerMonth,
      speds: plan.limits.spedsPerMonth,
      xmlScans: plan.limits.xmlScansPerMonth,
    };

    const limit = limitMap[metric];
    if (limit === undefined || limit === -1) return { used: usage[metric] || 0, limit: -1, pct: 0, isUnlimited: true };

    const used = usage[metric] || 0;
    return {
      used,
      limit,
      pct: limit > 0 ? (used / limit) * 100 : 0,
      isUnlimited: false,
      isOver: used >= limit,
    };
  }, [isGod, plan, usage]);

  return {
    subscription,
    plan,
    usage,
    loading,
    status,
    isTrial,
    isActive,
    isExpired,
    isPastDue,
    isGod,
    isLegacy,
    trialDaysLeft,
    canAccess,
    getUsageInfo,
    refresh,
    planName: plan?.name || subscription?.plan || null,
  };
}
